import { type ErrorInfo, type FC, type PropsWithChildren } from "react";
import { type DisplayableError } from "./DisplayableError";
import { type FallbackUI } from "./ErrorBoundaryComponent";
import { ErrorBoundaryWithTracking } from "./ErrorBoundaryComponent";
import { ErrorContextProvider, useErrorContext } from "./ErrorContext";
import { DefaultErrorUI } from "./DefaultErrorUi";

export type ErrorBoundaryProps = PropsWithChildren<{
    fallbackUI?: FallbackUI;
    trackException?: (error: Error | DisplayableError, errorInfo?: ErrorInfo) => void;
}>;

const InnerErrorBoundary: FC<Pick<ErrorBoundaryProps, "fallbackUI" | "children" | "trackException"> & { fallbackUI: FallbackUI }> = ({
    fallbackUI,
    children,
    trackException,
}) => {
    const errorContext = useErrorContext();

    return (
        <ErrorBoundaryWithTracking
            fallbackUI={fallbackUI}
            asyncErrorContext={errorContext}
            trackException={trackException}
        >
            {children}
        </ErrorBoundaryWithTracking>
    );
};

export const ErrorBoundaryWithAsyncContext: FC<ErrorBoundaryProps> = ({
    fallbackUI = DefaultErrorUI,
    trackException,
    children,
}) => {
    return (
        <ErrorContextProvider>
            <InnerErrorBoundary fallbackUI={fallbackUI} trackException={trackException}>
                {children}
            </InnerErrorBoundary>
        </ErrorContextProvider>
    );
};
